import './index.scss';
import moment from 'moment';
import { Button } from 'reactstrap';
import { toast } from 'react-toastify';
import Toast from '../../../shared/Toast/Toast';
import { Clock, MapPin } from 'react-feather';
import { useTranslation } from 'react-i18next';
import {
  GymResource,
  saveGymResource,
} from '../../../../store/gym-management/GymTypes';
import { UseFormReturn } from 'react-hook-form';
import React, { useEffect, useState } from 'react';
import { getSingleOption } from '../../../../utility/Utils';
import { useProfile } from '../../../../store/Auth/authHooks';
import BookGymDateInformation from '../RoomGymDateIformaton';
import { useCloseOldModal } from '../../../../store/modal/modalHooks';
import { ToastTypeEnum } from '../../../shared/Toast/toast-type.enum';
import { useCreditInUse } from '../../../../store/credits/creditsHooks';
import { MedicalStatusEnum } from '../BookFitness/medical-status.enum';
import { useBookGymRoom } from '../../../../store/gym-management/GymHooks';
import { formatNumberToDayName } from '../../../../utility/services/get-day-names';
import BookingCheckout, {
  CreditTypesEnum,
} from '../../../shared/BookingCheckout';
import {
  useFetchResourceAlloc,
  useResourceManagementSlots,
} from '../../../../store/RoomManagement/resourceManagementHooks';

type PropsTypes = {
  form: UseFormReturn<any, object>;
  gym: GymResource;
  setShowSuccessPage: (value: boolean) => void;
  setShowCreditsCardPage: (callBack: () => void) => void;
  setOrderId: (value: number) => void;
  setShowPaymentPage: (value: string) => void;
  setShowCancellationPolicy: (value: boolean) => void;
  showCancellationPolicy: boolean;
};

const BookGymForm = (props: PropsTypes) => {
  const profile = useProfile();
  const { t } = useTranslation();
  const closeModal = useCloseOldModal();
  const bookGymRoom = useBookGymRoom();
  const creditInUse = useCreditInUse();
  const [totalPrice, setTotalPrice] = useState(0);
  const resourceSlots = useResourceManagementSlots();
  const fetchResourceAlloc = useFetchResourceAlloc();

  const onBookGymRoom = async (data: any) => {
    const allowedStatuses = [
      MedicalStatusEnum.MEDICAL_FORM_VALID,
      MedicalStatusEnum.MEDICAL_FORM_NEARING_EXPIRATION,
      MedicalStatusEnum.MEDICAL_FORM_NEARING_EXPIRATION_SECOND,
      MedicalStatusEnum.MEDICAL_FORM_APPROVAL_PROCESS,
      MedicalStatusEnum.MEDICAL_FORM_NOT_APPROVE,
      MedicalStatusEnum.MEDICAL_FORM_CERTIFICATE_NEEDED,
    ];

    if (
      profile.medicalForm?.medicalStatus! ===
      MedicalStatusEnum.MEDICAL_FORM_WAITING_FOR_PERSONAL_ID
    ) {
      return Toast(ToastTypeEnum.ERROR, t('WAITING_FOR_PERSONAL_ID'), t);
    }

    if (!allowedStatuses.includes(profile.medicalForm?.medicalStatus!)) {
      return Toast(ToastTypeEnum.ERROR, t('NO_ALLOWED_MEDICAL_STATUS_TEXT'), t);
    }

    if (
      profile.medicalForm?.medicalStatus ===
      MedicalStatusEnum.MEDICAL_FORM_APPROVAL_PROCESS
    ) {
      return Toast(
        ToastTypeEnum.ERROR,
        t('MEDICAL_STATEMENT_IN_PROCESS_STATUS_TEXT'),
        t
      );
    }

    if (
      profile.medicalForm?.medicalStatus ===
      MedicalStatusEnum.MEDICAL_FORM_CERTIFICATE_NEEDED
    ) {
      return Toast(
        ToastTypeEnum.ERROR,
        t('MEDICAL_STATEMENT_NO_FILE_IN_UPLOAD'),
        t
      );
    }

    if (
      profile.medicalForm?.medicalStatus ===
      MedicalStatusEnum.MEDICAL_FORM_NOT_APPROVE
    ) {
      return Toast(
        ToastTypeEnum.ERROR,
        t('MEDICAL_STATEMENT_NOT_APPROVE_STATUS_TEXT'),
        t
      );
    }

    const newDate: saveGymResource = {
      resourceId: props.gym.id!,
      date: moment(data.date[0]).format('YYYY-MM-DD'),
      startTime: +data.start,
      endTime: +data.end,
      numberOfParticipants: 1,
    };

    if (creditInUse?.cashAmount) {
      props.setShowCreditsCardPage(async () => {
        bookGymRoom(newDate)
          .then((response) => {
            if (response.isPaid) {
              onSuccess();
            } else {
              props.setShowPaymentPage(response.paymentProcessLink!);
              props.setOrderId(response.orderId);
            }
          })
          .catch((error) => {
            onFailure(error as string);
          });
      });
      return;
    }
    try {
      const { isPaid, paymentProcessLink, orderId } = await bookGymRoom(
        newDate
      );
      if (isPaid) {
        onSuccess();
      } else {
        props.setShowPaymentPage(paymentProcessLink!);
        props.setOrderId(orderId);
      }
    } catch (error) {
      onFailure(error as string);
    }
  };

  const onSuccess = () => {
    props.setShowSuccessPage(true);
  };

  const onFailure = (errorMessage: string) => {
    toast.error(errorMessage, {
      icon: false,
      hideProgressBar: true,
      autoClose: 5000,
    });
  };

  useEffect(() => {
    if (props.form.watch('date')) {
      const timeStamp = new Date(props.form.watch('date')).getTime();
      fetchResourceAlloc(props.gym.id, timeStamp).finally();
    }
  }, [props.form.watch('date').toString()]);

  useEffect(() => {
    if (
      !props.form.watch('start') ||
      !props.form.watch('end') ||
      !props?.gym?.bookingPrice
    ) {
      setTotalPrice(0);
      return;
    }

    const price =
      (props.form.watch('end') - props.form.watch('start')) *
      props.gym.bookingPrice;

    setTotalPrice(price);
  }, [
    props.form.watch('start'),
    props.form.watch('end'),
    props.gym.bookingPrice,
  ]);

  const onShowPolicy = () => {
    if (props.showCancellationPolicy) {
      props.setShowCancellationPolicy(false);
    } else {
      props.setShowCancellationPolicy(true);
    }
  };

  return (
    <form onSubmit={props.form.handleSubmit(onBookGymRoom)}>
      <div className="book-gym-form">
        <div className="book-gym-form-content">
          <div className="book-gym-form-title">
            {t('GYM_ROOM_DETAILS_TITLE')}
          </div>
          <div className="book-gym-form-details">
            <div className="book-gym-form-detail-item">
              <div className="book-gym-form-detail-item-icon">
                <Clock size={18} />
              </div>
              <div className="book-gym-form-detail-item-location">
                <div className="book-gym-form-detail-item-location-name">
                  {getSingleOption(props.gym.availableFrom).label} -{' '}
                  {getSingleOption(props.gym.availableTo).label}
                </div>
                <div className="book-gym-form-detail-item-location-building">
                  {props.gym.availableDays.length === 7 && 'Whole seek'}
                  {props.gym.availableDays.length !== 7 &&
                    props.gym.availableDays
                      .sort((a, b) => a - b)
                      .map((day) => formatNumberToDayName(day))
                      .join(', ')}
                </div>
              </div>
            </div>
            <div className="book-gym-form-detail-item">
              <div className="book-gym-form-detail-item-icon">
                <MapPin size={18} />
              </div>
              <div className="book-gym-form-detail-item-location">
                <div className="book-gym-form-detail-item-location-name">
                  {props.gym?.location?.name}
                </div>
                <div className="book-gym-form-detail-item-location-building">
                  {props.gym.building?.name}
                </div>
              </div>
            </div>
          </div>
          {props.gym && (
            <BookGymDateInformation
              gymRoom={props.gym}
              form={props.form}
              originalArray={resourceSlots}
            />
          )}
          <div className="book-gym-prices">
            <BookingCheckout
              creditTypes={CreditTypesEnum.USER}
              price={totalPrice}
              disabledCreditUser={!props.gym.enableCreditUse}
            />
          </div>
        </div>
        <div className="book-gym-form-actions">
          <Button
            className="confirm-booking-gym-button"
            type="submit"
            color="primary"
            disabled={!props.form.watch('start') || !props.form.watch('end')}
          >
            {t('BOOK_GYM_TIME_BUTTON')}
          </Button>
          <Button
            className="cancel-booking-gym-button"
            type="button"
            color="transparent"
            onClick={closeModal}
          >
            {t('CLOSE_BUTTON')}
          </Button>
        </div>
        <div className="view-cancellation-policy-btn">
          <span onClick={() => onShowPolicy()}>
            {props.showCancellationPolicy
              ? t('BACK_BUTTON_TEXT')
              : t('VIEW_CANCELLATION_POLICY')}
          </span>
        </div>
      </div>
    </form>
  );
};

export default BookGymForm;
